/**
 * Admin triage hook (Batch 21a-3 §3).
 */
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";

interface TriageImpersonationConflict {
  impersonation_log_id: string;
  super_admin_user_id: string;
  super_admin_email: string | null;
  target_tenant_id: string | null;
  target_tenant_slug: string | null;
  target_user_id: string | null;
  started_at: string;
  reason: string | null;
}

interface TriageFailedSubscription {
  subscription_id: string;
  tenant_id: string;
  tenant_slug: string;
  status: string;
  past_due_since: string | null;
  last_event_at: string | null;
}

interface TriageZombieTenant {
  tenant_id: string;
  slug: string;
  name: string;
  pending_deletion_at: string | null;
  trial_ends_at: string | null;
  days_until_sweep: number | null;
}

interface TriageFailedSync {
  sync_log_id: string;
  integration_id: string;
  tenant_id: string;
  tenant_slug: string;
  direction: string;
  entity_type: string;
  status: string;
  error_message_redacted: string | null;
  has_raw_error_message: boolean;
  started_at: string;
  completed_at: string | null;
}

interface TriageResponse {
  impersonation_conflicts: TriageImpersonationConflict[];
  failed_subscriptions: TriageFailedSubscription[];
  zombie_tenants: TriageZombieTenant[];
  failed_greenhouse_syncs: TriageFailedSync[];
  counts: {
    impersonation_conflicts: number;
    failed_subscriptions: number;
    zombie_tenants: number;
    failed_greenhouse_syncs: number;
  };
}

export function useAdminTriage(category?: string) {
  return useQuery<TriageResponse, ApiError>({
    queryKey: ["admin", "triage", { category }],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (category) params["category"] = category;
      const r = await apiClient.get<TriageResponse>("/admin/triage", { params });
      return r.data;
    },
    refetchInterval: 60_000,
  });
}
